// Air-gap key management (master email only).
//
// An air-gap build is a per-customer image with every capability on. Its key is
// time-based and verified offline by that build only: past expiry the customer's
// admin portal warns — nothing degrades. Renewal = a new key, no rebuild.

import React, { useState, useEffect } from 'react';
import { Plus, RefreshCw, ChevronUp } from 'lucide-react';
import { api } from '../services/api';
import { LicenseKeyDisplay } from './KeyDisplay';
import type { AirgapKey } from '../types';

export function AirgapKeyManager() {
  const [licenses, setLicenses] = useState<AirgapKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);

  // Renew state
  const [renewId, setRenewId] = useState<string | null>(null);
  const [renewDays, setRenewDays] = useState(365);
  const [isRenewing, setIsRenewing] = useState(false);
  const [renewedKey, setRenewedKey] = useState<string | null>(null);

  // Form state
  const [customerId, setCustomerId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [durationDays, setDurationDays] = useState(365);
  const [buildId, setBuildId] = useState('');

  useEffect(() => {
    loadLicenses();
  }, []);

  const loadLicenses = async () => {
    try {
      const result = await api.listAirgapKeys();
      setLicenses(result.keys);
    } catch {
      // silently fail
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsGenerating(true);
    setGeneratedKey(null);

    try {
      const result = await api.generateAirgapKey({
        customerId: customerId.trim(),
        customerName: customerName.trim(),
        durationDays,
        buildId: buildId.trim() || null,
      });
      setGeneratedKey(result.licenseKey);
      setCustomerId('');
      setCustomerName('');
      setDurationDays(365);
      setBuildId('');
      await loadLicenses();
    } catch {
      alert('Failed to generate air-gap key.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRenew = async () => {
    if (!renewId) return;
    setIsRenewing(true);
    setRenewedKey(null);
    try {
      const result = await api.renewAirgapKey(renewId, renewDays);
      setRenewedKey(result.licenseKey);
      await loadLicenses();
    } catch {
      alert('Failed to renew license.');
    } finally {
      setIsRenewing(false);
    }
  };

  const activeLicenses = licenses.filter(l => !l.isExpired);
  const expiredLicenses = licenses.filter(l => l.isExpired);

  return (
    <div className="mt-12">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm font-medium text-accent uppercase tracking-wider">Air-gap Keys</p>
          <p className="text-sm text-muted-foreground mt-1">Time-based keys for per-customer air-gap builds. Expiry warns, never degrades.</p>
        </div>
        <button
          onClick={() => { setShowForm(!showForm); setGeneratedKey(null); }}
          className="inline-flex items-center gap-2 bg-accent text-white font-medium px-4 py-2 rounded-md hover:bg-accent/90 transition-all text-sm"
        >
          {showForm ? <ChevronUp className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showForm ? 'Close' : 'Generate Key'}
        </button>
      </div>

      {/* Generate form */}
      {showForm && (
        <div className="bg-card border border-border rounded-xl p-6 mb-6">
          {generatedKey ? (
            <div>
              <p className="text-sm font-medium text-foreground mb-3">Key generated. Set it as DIOSC_LICENSE on the customer's air-gap build. You can copy it again from the list below.</p>
              <LicenseKeyDisplay licenseKey={generatedKey} />
            </div>
          ) : (
            <form onSubmit={handleGenerate} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">Customer ID</label>
                  <input
                    type="text"
                    required
                    value={customerId}
                    onChange={(e) => setCustomerId(e.target.value)}
                    placeholder="acme-corp"
                    className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring/50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">Customer Name</label>
                  <input
                    type="text"
                    required
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="ACME Corporation"
                    className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring/50"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Duration (days)</label>
                <input
                  type="number"
                  required
                  min={1}
                  max={3650}
                  value={durationDays}
                  onChange={(e) => setDurationDays(parseInt(e.target.value) || 365)}
                  className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Build ID (optional)</label>
                <input
                  type="text"
                  value={buildId}
                  onChange={(e) => setBuildId(e.target.value)}
                  placeholder="bind to one stamped build"
                  className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring/50"
                />
              </div>
              </div>
              <button
                type="submit"
                disabled={isGenerating}
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-medium px-4 py-2 rounded-md hover:bg-primary/90 transition-all text-sm disabled:opacity-50"
              >
                {isGenerating ? 'Generating...' : 'Generate'}
              </button>
            </form>
          )}
        </div>
      )}

      {/* Renew modal */}
      {renewId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-foreground mb-2">Renew Air-gap Key</h3>
            <p className="text-sm text-muted-foreground mb-4">
              This generates a new key for the same customer (and build binding) with a fresh expiry. No rebuild is needed — the customer swaps DIOSC_LICENSE.
            </p>
            {renewedKey ? (
              <div>
                <p className="text-sm font-medium text-foreground mb-2">New license key:</p>
                <LicenseKeyDisplay licenseKey={renewedKey} />
                <div className="flex justify-end mt-4">
                  <button
                    onClick={() => { setRenewId(null); setRenewedKey(null); setRenewDays(365); }}
                    className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-muted-foreground mb-1">Duration (days)</label>
                  <input
                    type="number"
                    min={1}
                    max={3650}
                    value={renewDays}
                    onChange={(e) => setRenewDays(parseInt(e.target.value) || 365)}
                    className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => { setRenewId(null); setRenewDays(365); }}
                    disabled={isRenewing}
                    className="px-4 py-2 text-sm border border-border rounded-md text-foreground hover:bg-secondary transition-colors bg-transparent"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRenew}
                    disabled={isRenewing}
                    className="px-4 py-2 text-sm bg-accent text-white rounded-md hover:bg-accent/90 transition-colors disabled:opacity-50"
                  >
                    {isRenewing ? 'Renewing...' : 'Renew'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Active licenses list */}
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : activeLicenses.length === 0 ? (
        <p className="text-sm text-muted-foreground">No active air-gap keys.</p>
      ) : (
        <div className="space-y-3">
          {activeLicenses.map(lic => (
            <div key={lic.id} className="bg-card border border-border rounded-xl p-4 hover:border-accent/50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground truncate">{lic.customerName}</span>
                    <span className="text-xs text-muted-foreground font-mono">{lic.customerId}</span>
                    {lic.buildId && <span className="text-xs text-muted-foreground font-mono">build {lic.buildId}</span>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {lic.expiresAt && <>Expires: {new Date(lic.expiresAt).toLocaleDateString()}</>}
                    {' \u00b7 '}
                    Created: {new Date(lic.createdAt).toLocaleDateString()}
                  </div>
                </div>
                <button
                  onClick={() => setRenewId(lic.id)}
                  className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-accent transition-colors px-3 py-1.5 rounded-md hover:bg-secondary shrink-0"
                  title="Renew key"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Renew
                </button>
              </div>
              {lic.licenseKey && <LicenseKeyDisplay licenseKey={lic.licenseKey} />}
            </div>
          ))}
        </div>
      )}

      {/* Expired licenses */}
      {expiredLicenses.length > 0 && (
        <details className="mt-4">
          <summary className="text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
            {expiredLicenses.length} expired key{expiredLicenses.length !== 1 ? 's' : ''}
          </summary>
          <div className="space-y-3 mt-2">
            {expiredLicenses.map(lic => (
              <div key={lic.id} className="bg-card border border-border rounded-xl p-4 opacity-50">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground truncate line-through">{lic.customerName}</span>
                      <span className="text-xs text-muted-foreground font-mono">{lic.customerId}</span>
                      <span className="text-xs bg-red-500/20 text-red-400 border border-red-500/30 px-2 py-0.5 rounded">expired</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Expired: {lic.expiresAt && new Date(lic.expiresAt).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    onClick={() => setRenewId(lic.id)}
                    className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-accent transition-colors px-3 py-1.5 rounded-md hover:bg-secondary shrink-0"
                    title="Renew key"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Renew
                  </button>
                </div>
                {lic.licenseKey && <LicenseKeyDisplay licenseKey={lic.licenseKey} />}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
